import React, { useState, useEffect, useMemo } from "react";
import Cookies from "universal-cookie";
import { toast } from "react-toastify";
import {
  Pagination,
  Button,
  Modal,
  Tag,
  Typography,
  Space,
  Empty,
  Input,
  Card,
  Popconfirm,
  Tooltip,
  Dropdown,
  Menu,
  Badge,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  MoreOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UpOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { Checkbox } from "antd";
import { useDispatch } from "react-redux";
import { loaderReducer } from "../../components/toolkit/loader";
import "./index.css";

import {
  allRoles,
  roleDetails,
  deleteRoleApi,
  updateRoleApi,
  allModules,
} from "../../functions/role";

import Layout1 from "../../components/layout1";
import SubHeader from "../../components/subHeader";
import RoleAddModal from "./AddRoleModal";
import { useCan } from "../../utils/permissions";

const { Title, Text } = Typography;
const { Search } = Input;

const PAGE_SIZE = 10;

const MODULE_NAME_MAP = {
  answer: "Answers",
  appointment: "Appointments",
  auth: "Authentication",
  createnote: "Create Note",
  genericanswer: "Generic Answers",
  generictemplate: "Generic Templates",
  note: "Notes",
  notification: "Notifications",
  role: "Roles",
  signup: "Sign Up",
  surveytemplate: "Survey Templates",
  sync: "Sync",
  task: "Tasks",
  twofactor: "Two-Factor Authentication",
  user: "Users",
  workOrders: "Work Orders",
  workordertaskappointment: "Work Order Task Appointment",
};

export function humanizeModule(key) {
  if (!key && key !== 0) return "";
  // prefer exact map key (allows camelCase map keys like workOrders)
  if (MODULE_NAME_MAP[key]) return MODULE_NAME_MAP[key];

  // try lowercase lookup for snake_case or lowercase keys
  const lower = String(key).toLowerCase();
  const foundLower = Object.keys(MODULE_NAME_MAP).find(
    (k) => k.toLowerCase() === lower
  );
  if (foundLower) return MODULE_NAME_MAP[foundLower];

  // fallback: replace underscores/hyphens, split camelCase, then title-case
  const withSpaces = String(key)
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    // insert spaces between camelCase boundaries: fooBar -> foo Bar
    .replace(/([a-z])([A-Z])/g, "$1 $2");

  return withSpaces
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function normalizeForSearch(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function acronymOf(value) {
  const lettersOnly = String(value || "").replace(/[^a-zA-Z\s]/g, " ");
  return lettersOnly
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .toLowerCase();
}

function doesModuleMatchSearch(moduleKey, needle) {
  if (!moduleKey) return false;
  if (!needle) return true;
  const human = humanizeModule(moduleKey);
  const keyNorm = normalizeForSearch(moduleKey);
  const humanNorm = normalizeForSearch(human);
  const q = normalizeForSearch(needle);
  return keyNorm.includes(q) || humanNorm.includes(q);
}


function compareByName(a, b) {
  return String(a || "").localeCompare(String(b || ""), undefined, {
    sensitivity: "base",
    numeric: false,
  });
}

function sortPermissions(perms = []) {
  return [...(perms || [])].sort((p1, p2) =>
    humanizeModule(p1?.module).localeCompare(humanizeModule(p2?.module), undefined, {
      sensitivity: "base",
    })
  );
}


const RoleManagement = () => {
  const dispatch = useDispatch();
  const cookies = new Cookies();
  const authtoken = cookies.get("token");
  const can = useCan();
  const canRead = can("role", "read");
  const canCreate = can("role", "create");
  const canUpdate = can("role", "update");
  const canDelete = can("role", "delete");

  const [roles, setRoles] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRoles, setTotalRoles] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [q, setQ] = useState("");
  const [showPermissions, setShowPermissions] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [draftById, setDraftById] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [moduleKeys, setModuleKeys] = useState([]);

  // Permissions order for consistent grid
  const PERM_ORDER = ["read", "create", "update", "delete", "approve"];

  const handleEdit = (role) => {
    if (!canUpdate) return;
    // inline edit: expand and enable this card
    setExpanded((m) => ({ ...m, [role._id]: true }));
    setEditingId(role._id);
    setDraftById((prev) => ({
      ...prev,
      [role._id]: {
        name: role.name,
        permissions: (role.permissions || []).map((p) => ({
          module: p.module,
          actions: Array.isArray(p.actions) ? [...p.actions] : [],
        })),
      },
    }));
  };

  const applyDraftToRoles = (rolesList, roleId, draft) => {
    return rolesList.map((r) => {
      if (r._id === roleId) {
        return { ...r, name: draft.name, permissions: draft.permissions };
      }
      if (Array.isArray(r.subRole) && r.subRole.length > 0) {
        const updatedSubs = r.subRole.map((s) =>
          s._id === roleId ? { ...s, name: draft.name, permissions: draft.permissions } : s
        );
        return { ...r, subRole: updatedSubs };
      }
      return r;
    });
  };

  const handleSaveDraft = async (roleId) => {
    const draft = draftById[roleId];
    if (!draft) {
      setEditingId(null);
      return;
    }
    try {
      setSavingId(roleId);
      dispatch(loaderReducer(true));
      const payload = {
        name: draft.name,
        permissions: draft.permissions,
      };
      await updateRoleApi(authtoken, roleId, payload);
      toast.success("Role updated");
      await fetchRoles();
      setEditingId(null);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setSavingId(null);
      dispatch(loaderReducer(false));
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const togglePermission = (roleId, moduleKey, action, nextChecked) => {
    setDraftById((prev) => {
      const draft = prev[roleId];
      if (!draft) return prev;
      let found = false;
      const permissions = draft.permissions.map((p) => {
        if (p.module !== moduleKey) return p;
        found = true;
        const actionsSet = new Set(p.actions || []);
        if (nextChecked) actionsSet.add(action);
        else actionsSet.delete(action);
        return { ...p, actions: Array.from(actionsSet) };
      });
      const nextPermissions = found
        ? permissions
        : [...permissions, { module: moduleKey, actions: nextChecked ? [action] : [] }];
      return { ...prev, [roleId]: { ...draft, permissions: nextPermissions } };
    });
  };

  const handleAdd = () => {
    if (!canCreate) return;
    setEditingRole(null);
    setAddOpen(true);
  };

  // Fetch roles
  const fetchRoles = async () => {
    if (!canRead) return;
    dispatch(loaderReducer(true));
    setIsLoading(true);
    try {
      const res = await allRoles(authtoken);
      dispatch(loaderReducer(false));
      const results = res.data.roles || [];
      setRoles(results);
      setTotalRoles(results.length || 0);
    } catch (err) {
      dispatch(loaderReducer(false));
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setIsLoading(false);
    }
  };


  const fetchAllModules = async () => {
    try {
      const res = await allModules(authtoken);
      const list = Array.isArray(res?.data) ? res.data : [];
      const unique = Array.from(new Set(list));
      setModuleKeys(unique);
    } catch (err) {
    }
  };
  useEffect(() => {
    if (canRead) {
      fetchRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, canRead]);

  useEffect(() => {
    if (canRead) fetchAllModules();
  }, [canRead]);

  // collapse permissions by default; expand only on explicit user action (Edit/View)
  useEffect(() => {
    setExpanded({});
  }, [roles]);

  // when searching, page reset and expansion handled after `filtered` is computed

  // Delete role
  const handleDelete = async (id) => {
    if (!canDelete) return;
    setIsLoading(true);
    dispatch(loaderReducer(true));
    try {
      await deleteRoleApi(authtoken, id);
      toast.success("Role deleted successfully");
      await fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setIsLoading(false);
      dispatch(loaderReducer(false));
    }
  };

  // prepare table rows (include subroles as separate rows, keep ordering stable)
  const getTableData = (rolesList) => {
    const data = [];
    const roleKeys = new Set();

    const sortedRoles = [...(rolesList || [])].sort((a, b) => compareByName(a?.name, b?.name));

    sortedRoles.forEach((role) => {
      if (!roleKeys.has(role._id)) {
        data.push({
          key: role._id,
          name: role.name,
          permissions: sortPermissions(role.permissions || []),
          subRole: (role.subRole || []).slice(),
          _id: role._id,
          isSub: false,
        });
        roleKeys.add(role._id);
      }

      if (role.subRole?.length > 0) {
        const sortedSubs = [...role.subRole].sort((a, b) => compareByName(a?.name, b?.name));
        sortedSubs.forEach((subRole) => {
          if (!roleKeys.has(subRole._id)) {
            data.push({
              key: subRole._id,
              name: subRole.name,
              permissions: sortPermissions(subRole.permissions || []),
              subRole: (subRole.subRole || []).slice(),
              _id: subRole._id,
              isSub: true,
              parentName: role.name,
            });
            roleKeys.add(subRole._id);
          }
        });
      }
    });
    return data;
  };

  // Search / filter logic
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return getTableData(roles);
    const all = getTableData(roles);
    return all
      .map((r) => {
        const roleNameMatch = (r.name || "").toLowerCase().includes(needle) || (r.parentName || "").toLowerCase().includes(needle);
        // If searching by role name, show all permissions. If searching by module/action, narrow.
        const nextPermissions = roleNameMatch
          ? r.permissions
          : (r.permissions || []).filter((p) => {
            const moduleMatches = doesModuleMatchSearch(p.module, needle);
            const actionsMatch = Array.isArray(p.actions) && p.actions.join(",").toLowerCase().includes(needle);
            return moduleMatches || actionsMatch;
          });

        return {
          ...r,
          permissions: sortPermissions(nextPermissions || []),
        };
      })
      .filter((r) => {
        if ((r.name || "").toLowerCase().includes(needle)) return true;
        if ((r.parentName || "").toLowerCase().includes(needle)) return true;
        // row matches if any permission matches module or action
        if ((r.permissions || []).length > 0) return true;
        return false;
      });
  }, [q, roles]);

  // renderer for permission matrix rows
  const renderPermissions = (permissions = [], isEditing = false, roleId = null) => {
    if (!showPermissions) return null;

    if (!isEditing) {
      if (!permissions || permissions.length === 0) {
        return <Text type="secondary">No permissions set</Text>;
      }
      const rows = permissions;
      return (
        <div style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 0,
              minWidth: 900,
              maxHeight: 400,
              overflowY: "auto",
              overflowX: "auto",
              border: "1px solid #f0f0f0",
              borderRadius: 12,
              background: "#fff",
              position: "relative",
            }}
          >
            {/* header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.5fr repeat(5, 120px)",
                alignItems: "center",
                columnGap: 12,
                position: "sticky",
                top: 0,
                background: "#fafafa",
                zIndex: 10,
                padding: "10px 12px",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <span style={{ fontWeight: 500 }}>Module</span>
              {PERM_ORDER.map((a) => (
                <span key={a} style={{ textAlign: "center", textTransform: "capitalize", fontSize: 13, fontWeight: 500 }}>
                  {a}
                </span>
              ))}
            </div>
            {/* rows */}
            {rows.map((p, idx) => (
              <div
                key={p.module}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr repeat(5, 120px)",
                  alignItems: "center",
                  columnGap: 12,
                  background: idx % 2 === 0 ? "#fafafa" : "#fff",
                  padding: "8px 12px",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <div style={{ overflow: "hidden", padding: "8px 12px", lineHeight: 1.4 }}>{humanizeModule(p.module)}</div>
                {PERM_ORDER.map((action) => {
                  const has = Array.isArray(p.actions) && p.actions.includes(action);
                  return (
                    <div key={action} style={{ display: "flex", justifyContent: "center" }}>
                      <Tooltip
                        title={`${humanizeModule(p.module)}  ${action.charAt(0).toLocaleLowerCase() + action.slice(1)} ${has ? "allowed" : "denied"}`}
                      >
                        <div
                          aria-label={`${humanizeModule(p.module)} ${action}`}
                          role="checkbox"
                          aria-checked={!!has}
                          style={{
                            width: 28,
                            height: 28,
                            border: "1px solid #e6e6e6",
                            borderRadius: 6,
                            background: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "default",
                          }}
                        >
                          <Checkbox
                            checked={!!has}
                            disabled
                            className="perm-checkbox"
                            style={{ transform: "scale(1.05)", cursor: "default" }}
                          />
                        </div>
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if ((!permissions || permissions.length === 0) && (!moduleKeys || moduleKeys.length === 0)) {
      return <Text type="secondary">No permissions set</Text>;
    }
    const baseList = (moduleKeys && moduleKeys.length > 0)
      ? moduleKeys
      : (permissions || []).map((p) => p.module);
    const permSource = (roleId ? (draftById[roleId]?.permissions || permissions) : permissions) || [];
    const rows = baseList.map((m) => {
      const found = permSource.find((p) => p.module === m);
      return { module: m, actions: Array.isArray(found?.actions) ? found.actions : [] };
    });
    rows.sort((a, b) => humanizeModule(a.module).localeCompare(humanizeModule(b.module), undefined, { sensitivity: "base" }));
    return (
      <div style={{ width: "100%", overflowX: "auto" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 0,
            minWidth: 900,
            border: "1px solid #eee",
            borderRadius: 12,
            overflow: "hidden",
            background: "#fff",
          }}
        >
          {/* header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.5fr repeat(5, 120px)",
              alignItems: "center",
              columnGap: 12,
              position: "sticky",
              top: 0,
              background: "#fff",
              zIndex: 1,
              padding: "10px 12px",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <span style={{ fontWeight: 500 }}>Module</span>
            {PERM_ORDER.map((a) => (
              <span key={a} style={{ textAlign: "center", textTransform: "capitalize", fontSize: 13, fontWeight: 500 }}>
                {a}
              </span>
            ))}
          </div>
          {/* rows */}
          {rows.map((p, idx) => (
            <div
              key={p.module}
              style={{
                display: "grid",
                gridTemplateColumns: "1.5fr repeat(5, 120px)",
                alignItems: "center",
                columnGap: 12,
                background: idx % 2 === 0 ? "#fafafa" : "#fff",
                padding: "8px 12px",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <div style={{ overflow: "hidden", padding: "8px 12px", lineHeight: 1.4 }}>{humanizeModule(p.module)}</div>
              {PERM_ORDER.map((action) => {
                const has = Array.isArray(p.actions) && p.actions.includes(action);
                return (
                  <div key={action} style={{ display: "flex", justifyContent: "center" }}>
                    <Tooltip
                      title={`${humanizeModule(p.module)}  ${action.charAt(0).toLocaleLowerCase() + action.slice(1)} ${has ? "allowed" : "denied"}`}
                    >
                      <div
                        aria-label={`${humanizeModule(p.module)} ${action}`}
                        role={isEditing ? "switch" : "checkbox"}
                        aria-checked={!!has}
                        style={{
                          width: 28,
                          height: 28,
                          border: "1px solid #e6e6e6",
                          borderRadius: 6,
                          background: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: isEditing ? "pointer" : "default",
                        }}
                      >
                        <Checkbox
                          checked={!!has}
                          disabled={!isEditing}
                          className="perm-checkbox"
                          onChange={(e) => isEditing && roleId && togglePermission(roleId, p.module, action, e.target.checked)}
                          style={{ transform: "scale(1.05)", cursor: isEditing ? "pointer" : "default" }}
                        />
                      </div>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // pagination slice
  const pageData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  // when searching, auto-expand matching roles and reset to page 1
  useEffect(() => {
    const needle = q.trim();
    if (needle) {
      const next = {};
      (filtered || []).forEach((r) => {
        if (r && r._id) next[r._id] = true;
      });
      setExpanded(next);
      setPage(1);
    } else {
      setExpanded({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, filtered]);

  return (
    <Layout1>
      <SubHeader />
      <div style={{ padding: 20 }}>
        <Card bodyStyle={{ padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <Title level={4} style={{ margin: 0 }}>Role Management</Title>
              <Text type="secondary">Manage roles, sub-roles and permissions</Text>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Search
                placeholder="Search role, module "
                value={q}
                onChange={(e) => setQ(e.target.value)}
                allowClear
                style={{ width: 360 }}
                prefix={<SearchOutlined />}
              />



              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                disabled={!canCreate}
              >
                Add Role
              </Button>
            </div>
          </div>

          {/* Card-based list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {pageData.length === 0 && !isLoading && (
              <Empty description="No roles found" />
            )}
            {pageData.map((record) => {
              const isOpen = !!expanded[record._id];
              const isEditing = editingId === record._id;
              return (
                <Card key={record._id} bodyStyle={{ padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {isEditing ? (
                          <Input
                            size="small"
                            value={draftById[record._id]?.name ?? record.name}
                            onChange={(e) =>
                              setDraftById((prev) => ({
                                ...prev,
                                [record._id]: {
                                  ...(prev[record._id] || { name: record.name, permissions: record.permissions }),
                                  name: e.target.value,
                                },
                              }))
                            }
                            style={{ width: 260 }}
                          />
                        ) : (
                          <Text strong>{record.name}</Text>
                        )}
                        {record.isSub && record.parentName && (
                          <Tooltip title={`Parent: ${record.parentName}`}>
                            <Tag size="small">sub</Tag>
                          </Tooltip>
                        )}
                        {record.subRole?.length > 0 && (
                          <Tag size="small" color="purple">{record.subRole.length} sub</Tag>
                        )}
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>ID: {record._id}</Text>
                    </div>

                    <Space>
                      {!isEditing ? (
                        <>
                          <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                            disabled={!canUpdate}
                          >
                            Edit
                          </Button>
                          <Popconfirm title="Delete role?" okText="Yes" cancelText="No" onConfirm={() => handleDelete(record._id)}>
                            <Button size="small" icon={<DeleteOutlined />} disabled={!canDelete}>Delete</Button>
                          </Popconfirm>
                        </>
                      ) : (
                        <>
                          <Button size="small" type="primary" loading={savingId === record._id} onClick={() => handleSaveDraft(record._id)}>Save</Button>
                          <Button size="small" onClick={handleCancelEdit}>Cancel</Button>
                        </>
                      )}
                      <Button onClick={() => setExpanded((m) => ({ ...m, [record._id]: !isOpen }))}>
                        {isOpen ? "Hide permissions" : "View permissions"}
                      </Button>
                    </Space>
                  </div>

                  {isOpen && renderPermissions(record.permissions, isEditing, record._id)}
                </Card>
              );
            })}
          </div>

          <div style={{ marginTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Text type="secondary">{filtered.length} roles</Text>
            <Pagination
              total={filtered.length}
              current={page}
              pageSize={PAGE_SIZE}
              onChange={(p) => setPage(p)}
              showSizeChanger={false}
            />
          </div>
        </Card>

        {/* Modals */}


        <RoleAddModal
          open={addOpen}
          onClose={(didSave) => {
            setAddOpen(false);
            if (didSave) fetchRoles();
          }}
        />
      </div>
    </Layout1>
  );
};

export default RoleManagement;

